<template>
  <div
    class="inline-flex w-full max-w-md select-none"
    :class="disabled ? 'opacity-50 cursor-not-allowed' : ''"
  >
    <div class="w-full">
      <!-- Label slot -->
      <label v-if="$slots.label" :for="id" class="block mb-1 text-center text-lg sm:text-xl font-bold text-slate-700">
        <slot name="label" />
      </label>

      <!-- Slider root -->
      <div
        class="relative h-8"
        @mousedown="onPointerDown"
        @touchstart.prevent="onPointerDown"
      >
        <!-- Track -->
        <div
          class="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-slate-200"
        ></div>

        <!-- Filled track 
        <div
          class="absolute left-0 top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-sky-500"
          :style="{ width: percentage + '%' }"
        ></div>-->

        <!-- Thumb (button for a11y) -->
        <button
          :id="id"
          type="button"
          class="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-5 w-5 rounded-full bg-sky-500 border border-slate-300 shadow focus:outline-none focus:ring-2 focus:ring-sky-500"
          :style="{ left: percentage + '%' }"
          role="slider"
          :aria-valuemin="min"
          :aria-valuemax="max"
          :aria-valuenow="modelValueNum"
          :aria-valuetext="ariaValueText"
          :aria-disabled="disabled ? 'true' : 'false'"
          :tabindex="disabled ? -1 : 0"
          @keydown="onKeydown"
        ></button>

        <!-- Optional tooltip -->
        <div v-if="showTooltip" class="absolute -top-7" :style="{ left: percentage + '%' }">
          <div class="-translate-x-1/2 rounded bg-gray-800 px-2 py-0.5 text-xs text-white">
            {{ displayValue }}
          </div>
        </div>
      </div>

      <!-- Helper text slot -->
      <p v-if="$slots.help" class="mt-1 text-xs text-gray-500">
        <slot name="help" />
      </p>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable vue/multi-word-component-names */
/* global defineProps, defineEmits */
import { computed, toRef } from 'vue'

// Use runtime props (no TypeScript) to avoid TS loader requirements in webpack
const props = defineProps({
  modelValue: { type: [Number, String], required: true },
  min: { type: Number, default: 0 }, // hours lower bound
  max: { type: Number, default: 23 }, // hours upper bound
  step: { type: Number, default: 1 },
  disabled: { type: Boolean, default: false },
  showTooltip: { type: Boolean, default: true },
  // optional formatter function
  format: { type: Function, default: (v) => `${v}` },
  id: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue', 'change'])

const min = computed(() => props.min)
const max = computed(() => props.max)
const step = computed(() => props.step)
const isDisabled = toRef(props, 'disabled')
const id = computed(() => props.id || `slider-${Math.random().toString(36).slice(2, 8)}`)

const clamp = (n, lo, hi) => Math.min(Math.max(n, lo), hi)

function roundToStep(n, base, inc) {
  const q = Math.round((n - base) / inc)
  const v = base + q * inc
  return Number(v.toFixed(6))
}

const modelValueNum = computed({
  get() {
    const num = typeof props.modelValue === 'string' ? Number(props.modelValue) : props.modelValue
    const c = clamp(Number.isFinite(num) ? num : 0, min.value, max.value)
    return roundToStep(c, min.value, step.value)
  },
  set(v) {
    if (isDisabled.value) return
    const c = clamp(roundToStep(v, min.value, step.value), min.value, max.value)
    emit('update:modelValue', c)
  },
})

const percentage = computed(() => ((modelValueNum.value - min.value) / (max.value - min.value)) * 100)

const displayValue = computed(() => (props.format ? props.format(modelValueNum.value) : modelValueNum.value))
const ariaValueText = computed(() => (props.format ? props.format(modelValueNum.value) : String(modelValueNum.value)))

function valueFromPointer(clientX, el) {
  const rect = el.getBoundingClientRect()
  const ratio = clamp((clientX - rect.left) / rect.width, 0, 1)
  const raw = min.value + ratio * (max.value - min.value)
  return roundToStep(raw, min.value, step.value)
}

function onPointerDown(e) {
  if (isDisabled.value) return
  const root = e.currentTarget
  const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
  modelValueNum.value = valueFromPointer(clientX, root)

  const move = (ev) => {
    const x = 'touches' in ev ? ev.touches[0].clientX : ev.clientX
    modelValueNum.value = valueFromPointer(x, root)
  }
  const up = () => {
    window.removeEventListener('mousemove', move)
    window.removeEventListener('touchmove', move)
    window.removeEventListener('mouseup', up)
    window.removeEventListener('touchend', up)
    emit('change', modelValueNum.value)
  }
  window.addEventListener('mousemove', move)
  window.addEventListener('touchmove', move, { passive: false })
  window.addEventListener('mouseup', up)
  window.addEventListener('touchend', up)
}

function onKeydown(e) {
  if (isDisabled.value) return
  let delta = 0
  switch (e.key) {
    case 'ArrowRight':
    case 'ArrowUp':
      delta = step.value
      break
    case 'ArrowLeft':
    case 'ArrowDown':
      delta = -step.value
      break
    case 'PageUp':
      delta = Math.max(step.value, (max.value - min.value) / 10)
      break
    case 'PageDown':
      delta = -Math.max(step.value, (max.value - min.value) / 10)
      break
    case 'Home':
      modelValueNum.value = min.value
      e.preventDefault()
      emit('change', modelValueNum.value)
      return
    case 'End':
      modelValueNum.value = max.value
      e.preventDefault()
      emit('change', modelValueNum.value)
      return
    default:
      return
  }
  e.preventDefault()
  modelValueNum.value = clamp(modelValueNum.value + delta, min.value, max.value)
  emit('change', modelValueNum.value)
}
</script>

<style scoped>
/* Optional: increase hit area on mobile without changing visual size */
button[role="slider"]::after {
  content: '';
  position: absolute;
  inset: -8px;
}
</style>
